import { useCallback, useMemo, useState } from "react";
import api from "../../../../services/api";
import { useParams } from "react-router-dom";
import { ErrorMessage, SuccessMessage } from '../../../../utils/alerts';
import { useTranslation } from "react-i18next";

const allMenuOptionsBasedOnPlanMeta = [
  {
    key: 'tele_enabled',
    label: 'Profissionais',
    value: 'prof_enabled'
  },
  {
    key: 'sipat_enabled',
    label: 'SIPAT',
    value: 'sipat_enabled'
  },
  {
    key: 'mood_enabled',
    label: 'Diário Emocional',
    value: 'mood_enabled'
  },
  {
    key: 'gym_enabled',
    label: 'Namu Pass',
    value: 'gym_enabled'
  },
  {
    key: 'healthscan_enabled',
    label: 'Vital Scan',
    value: 'healthscan_enabled'
  },
  {
    key: 'platescan_enabled',
    label: 'Food Scan',
    value: 'platescan_enabled'
  },
  {
    key: 'chat_enabled',
    label: 'Manu Chat bot',
    value: 'chat_enabled'
  },
  {
    key: 'namuchef_enabled',
    label: 'Namu Chef',
    value: 'namuchef_enabled'
  },
  {
    key: 'nutri360_enabled',
    label: 'Nutri360',
    value: 'nutri360_enabled'
  },
  {
    key: 'books_enabled',
    label: 'Livros',
    value: 'books_enabled'
  },
];

const moreOptions = [
  {
    key: 'blog',
    label: 'Blog',
    value: 'blog'
  },
  {
    key: 'schedule',
    label: 'Agenda',
    value: 'schedule'
  },
  {
    key: 'profile',
    label: 'Perfil',
    value: 'profile'
  },
  {
    key: 'home',
    label: 'Início',
    value: 'home',
    hidden: true,
  },
  {
    key: 'lessons',
    label: 'Aulas',
    value: 'lessons',
    hidden: true,
  },
];

export const useMenu = ({ planMeta }) => {
  const params = useParams();
  const { t } = useTranslation("common");

  const planMenu = planMeta?.find(item => item.key === 'custom_menu')?.value;
    
  const getMenuValue = useCallback((keys, fallbackValue) => {
    const foundItem = planMeta?.find(item => keys.includes(item.key) && item.value);
    if (foundItem && ['professional_enabled', 'telemedicine_enabled'].includes(foundItem.key)) {
      return 'tele_enabled';
    }
    return foundItem ? foundItem.key : fallbackValue;
  }, [planMeta]);
  
  const menuOptions = useMemo(() => {
    const enabledOptions = allMenuOptionsBasedOnPlanMeta.filter(option => {
      if (option.value === 'prof_enabled') {
        const obj = planMeta?.find(plan => ['professional_enabled', 'telemedicine_enabled'].includes(plan.key) && plan?.value);
        return !!obj?.value;
      }
      const planOption = planMeta?.find(plan => plan.key === option.key);

      return !!planOption?.value;
    }).reduce((acc, item) => {
      if (item.value === 'prof_enabled') {
        acc.push({
          ...item,
          value: 'tele_enabled'
        })
      }
      if (!acc.some(existingItem => existingItem.label === item.label)) {
        acc.push(item);
      };

      return acc;
    }, []);

    return enabledOptions.concat(moreOptions);
  }, [planMeta]);
  
  const defaultMenu = useMemo(() => {
    const menuPlanArray = Object.keys(planMenu).map(key => {
      const item = menuOptions.find(option => option.value === key);
      if (item) {
        return { position: planMenu[key], value: key, label: item.label, hidden: key === 'home' || key === 'lessons' };
      }

      return { position: planMenu[key], value: key, hidden: key === 'home' || key === 'lessons' };
    });
    
    if (!menuPlanArray.find(item => item.value === 'home')) {
      menuPlanArray.unshift({ position: 1, value: 'home', label: 'Início', hidden: true });
    }

    if (!menuPlanArray.find(item => item.position === 2)) {
      menuPlanArray.splice(1, 0, { position: 2, value: 'blog' });
    }

    if (!menuPlanArray.find(item => item.value === 'lessons')) {
      menuPlanArray.push({ position: 3, value: 'lessons', label: 'Aulas', hidden: true });
    }

    if (!menuPlanArray.find(item => item.position === 4)) {
      menuPlanArray.push({ position: 4, value: getMenuValue(['gym_enabled'], 'schedule') });
    }

    if (!menuPlanArray.find(item => item.position === 5)) {
      menuPlanArray.push({ position: 5, value: getMenuValue(['professional_enabled', 'telemedicine_enabled'], 'profile') });
    }

    return menuPlanArray.sort((a, b) => a.position - b.position);
  }, [planMenu, menuOptions, getMenuValue]);

  const [customMenu, setCustomMenu] = useState(defaultMenu);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleChangeItem = useCallback((oldItem, newItem) => {
    const newMenu = customMenu.map(item => {
      if (item.position === oldItem.position) {
        return {
          ...item,
          value: newItem,
          label: menuOptions.find(option => option.value === newItem)?.label || item.label,
        };
      };

      return item;
    });

    setCustomMenu(newMenu);
  }, [customMenu, menuOptions]);

  const updateMenu = useCallback(async () => {
    setIsUpdating(true);

    const menuObject = customMenu.reduce((acc, item) => {
      acc[item.value] = item.position;
      return acc;
    }, {});

    try {
      const { data } = await api.put(`/businesses/${params.businessId}/menu-customization`, { data: menuObject });

      if (data.success) {
        SuccessMessage({ message: t("save_successfully") });
      }
    } catch (error) {
      console.error('Error updating menu customization:', error);
      ErrorMessage({ message: t("save_error") });
    } finally {
      setIsUpdating(false);
    }
  }, [params, customMenu]);

  return {
    customMenu,
    handleChangeItem,
    menuOptions,
    updateMenu,
    isUpdating,
  }
};
